import React, { FunctionComponent, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Container from "aws-northstar/layouts/Container";
import FormRenderer, { componentTypes } from "aws-northstar/components/FormRenderer";
import Flashbar, { FlashbarMessage } from "aws-northstar/components/Flashbar";
import { useSelector } from "react-redux";

import { createRequest } from "../../data";
import { IRequest, IUserInfo, ReduxRoot } from "../../interfaces";
import { translateAccountId } from "../../config";

export type FormData = {
    requester?: string;
    request_account?: string;
    request_role?: string;
    request_duration?: string;
    request_justification?: string;
};

type field = { label: string; value: string };

const RequestForm: FunctionComponent = () => {
    const userInfo = useSelector((state: ReduxRoot) => {
        return state.breakGlassReducerState.userInfo;
    });

    const [justification, setJustification] = React.useState<string>("");
    const [accounts, setAccounts] = React.useState<field[]>([{ label: "", value: "" }]);
    const [account, setAccount] = React.useState<field>({ label: "", value: "" });
    const [roles, setRoles] = React.useState<field[]>([]);
    const [role, setRole] = React.useState<field>({ label: "", value: "" });
    const [duration, setDuration] = React.useState("");
    const [errors, setErrors] = React.useState<FlashbarMessage[]>([]);

    const onJustificationChange = (value: string) => {
        setJustification(value);
    };

    const onAccountChange = (account: { label: string; value: string }) => {
        if (account == undefined) {
            return;
        }
        setAccount(account);
    };

    const onRoleChange = (role: field) => {
        setRole(role);
    };

    const onDurationChange = (value: string) => {
        setDuration(value);
    };

    const formSchema = {
        header: "Temporary elevated access request form",
        description: "Raise temporary elevated access request here",
        fields: [
            {
                component: componentTypes.TEXT_FIELD,
                name: "justificationField",
                label: "Justification for requesting temporary elevated access",
                helperText: "Maximum 1000 characters",
                isRequired: true,
                resolveProps: (_props: any, _field: any, formOptions: any) => {
                    onJustificationChange(formOptions.getState().values.justificationField);

                    return {};
                },
            },
            {
                component: componentTypes.SELECT,
                name: "accountField",
                label: "Account requesting access for",
                helperText: "Select from accounts you have access to",
                placeholder: "Choose the account",
                options: accounts,
                isRequired: true,
                isSearchable: true,
                isClearable: true,
                resolveProps: (_props: any, _field: any, formOptions: any) => {
                    if (formOptions.getState().values.accountField != account) {
                        onAccountChange(formOptions.getState().values.accountField);
                        formOptions.change("roleField", undefined);
                        setRole({ label: "", value: "" });
                    }

                    return {};
                },
            },
            {
                component: componentTypes.SELECT,
                name: "roleField",
                label: "Role requesting access to",
                placeholder: "Choose the role",
                helperText: "Select from roles you have access to, please select an account first",
                options: roles,
                isRequired: true,
                isSearchable: true,
                isClearable: true,
                resolveProps: (_props: any, _field: any, formOptions: any) => {
                    //console.log(roles);
                    onRoleChange(formOptions.getState().values.roleField);

                    return formOptions.getState().values.accountField
                        ? {
                              options: roles,
                              isDisabled: false,
                          }
                        : { options: [], isDisabled: true };
                },
            },
            {
                component: componentTypes.TEXT_FIELD,
                name: "durationField",
                label: "Requested duration for elevated access in minutes",
                type: "number",
                helperText: "Maximum 480 minutes",
                initialValue: duration,
                //onChange: onDurationChange,
                resolveProps: (_props: any, _field: any, formOptions: any) => {
                    onDurationChange(formOptions.getState().values.durationField);

                    return {};
                },
            },
        ],
    };

    const history = useHistory();

    const onSubmit = async (_data: any) => {
        if (!validateForm()) {
            return;
        }

        try {
            //console.log(account.value, role.value, duration, justification);
            await createRequest(account.value, role.value, duration, justification).then((result: IRequest[]) => {
                return result;
            });
            history.goBack();
            history.push("/Request-dashboard");
        } catch (err) {
            if (justification === "") {
                const items: FlashbarMessage[] = [
                    {
                        header: "Could not create the request: " + err.toString(),
                        type: "error",
                        dismissible: true,
                    },
                ];

                setErrors(items);
            }
        }
    };

    const validateForm = () => {
        let error = false;
        const errorsFound: FlashbarMessage[] = [];

        if (justification === "") {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data : Please provide a justification",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        }

        if (account.value === "") {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data : Please select an account",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        }
        if (role.label === "" || role.value === "") {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data : Please select a role",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        } else if (userInfo.accountMap.has(account.value)) {
            const roles: Array<string> = userInfo.accountMap.get(account.value);

            if (!roles.includes(role.value)) {
                const item: FlashbarMessage = {
                    header: "Form has Invalid Data: Please select a role associated with the account",
                    type: "error",
                    dismissible: true,
                };

                errorsFound.push(item);
                error = true;
            }
        } else {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data: Selected account does not exist",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        }

        if (duration === "") {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data : Please provide a duration",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        } else if (parseInt(duration) <= 0 || parseInt(duration) > 480) {
            const item: FlashbarMessage = {
                header: "Form has Invalid Data : Please provide a positive value less than 480 for duration",
                type: "error",
                dismissible: true,
            };

            errorsFound.push(item);
            error = true;
        }

        if (error) {
            setErrors(errorsFound);

            return false;
        } else {
            return true;
        }
    };

    useEffect(() => {
        setAccounts(getAccountsValues(userInfo));
    }, [userInfo]);

    useEffect(() => {
        setRoles(getRoleValues(account.value, userInfo));
    }, [account]);

    return (
        <Container>
            <FormRenderer
                schema={formSchema}
                subscription={{ values: true }}
                onCancel={() => history.goBack()}
                onSubmit={onSubmit}
            />
            <Flashbar items={errors} />
        </Container>
    );
};

function getAccounts(userInfo: IUserInfo) {
    const list: Array<any> = [];

    //console.log("Size of Account Map : " + userInfo.accountMap.size);
    userInfo.accountMap.forEach(function (value, key) {
        //console.log("Key in Api AccountMap : " + key);
        list.push(key);
    });

    return list;
}

function getAccountsValues(userInfo: IUserInfo) {
    const accounts = getAccounts(userInfo);
    const list: Array<any> = [];

    for (const account of accounts) {
        const friendlyName = translateAccountId(account);
        const displayLabel = friendlyName !== account ? `${friendlyName} (${account})` : account;

        list.push({ label: displayLabel, value: account });
    }

    return list;
}

function getRoleValues(account: string, userInfo: IUserInfo) {
    const roles = userInfo.accountMap.get(account);
    const list: Array<field> = [];

    for (const role of roles ? roles : []) {
        //console.log("Role : " + role)
        list.push({ label: role, value: role });
    }

    return list;
}

export default RequestForm;
